import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { FxAlert, FxButton, FxSegmentedTabs, FxSpinner } from '@/components/fx'
import { CockpitShareDialog } from '@/components/share'
import { AddDocumentsDialog } from '@/components/upload'
import { CockpitHeader } from './CockpitHeader'
import { CockpitProvider, useCockpit, useCockpitDialog } from './useCockpit'

/**
 * The workspace of one template: a header, four tabs and whichever tab is
 * open. A react-router **layout route** nested inside `AppShell`, so the rail
 * stays put while the tabs change and each tab is a real URL.
 *
 * The header and the tab strip live here rather than in the tabs because they
 * are identical on all four artboards — `Cockpit`, `Datensaetze`, `Vorlage`
 * and `CockpitEinstellungen` draw the same band — and because the tab strip
 * has to know the document count, which is the layout's query.
 *
 * Server state is loaded once here (`useCockpit`) and handed down through
 * `CockpitProvider`; a tab reads it with `useCockpitContext()` and never
 * fetches the template again. See `useCockpit.ts` for the keys and the
 * polling policy.
 */

export interface CockpitTab {
  /** The path segment under `/templates/:templateId`; '' is the index route. */
  segment: string
  label: string
}

export const COCKPIT_TABS: CockpitTab[] = [
  { segment: '', label: 'Dokumente' },
  { segment: 'datensaetze', label: 'Datensätze' },
  { segment: 'vorlage', label: 'Vorlage' },
  { segment: 'einstellungen', label: 'Einstellungen' },
]

/**
 * Which tab a path is on.
 *
 * Anything that is not one of the four — `/dokumente/:documentId`,
 * `/unklarheiten`, `/bearbeiten/:page` — is a screen of its own that happens
 * to live under the same prefix, and selects no tab rather than guessing one.
 * Marking "Dokumente" current while the review screen is open would be a
 * screen reader reading out a wrong answer confidently.
 */
export function cockpitTabFromPath(pathname: string, templateId: string): string | null {
  const prefix = `/templates/${templateId}`
  if (!pathname.startsWith(prefix)) return null
  const rest = pathname.slice(prefix.length).replace(/^\/+|\/+$/g, '')
  if (rest === '') return ''
  const tab = COCKPIT_TABS.find((candidate) => candidate.segment !== '' && candidate.segment === rest)
  return tab ? tab.segment : null
}

export function CockpitLayout() {
  const { templateId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const cockpit = useCockpit(templateId)
  const { dialog, close } = useCockpitDialog()

  const activeTab = cockpitTabFromPath(location.pathname, templateId)
  const total = cockpit.counts?.total
  const items = COCKPIT_TABS.map((tab) => ({
    value: tab.segment,
    // Only the Dokumente tab carries a count, and only once it is known — a
    // "(0)" while the request is in flight reads as an empty form.
    label: tab.segment === '' && total != null ? `${tab.label} (${total})` : tab.label,
  }))

  const status = (cockpit.error as { status?: number } | null)?.status

  return (
    <CockpitProvider value={cockpit}>
      <div className="flex min-h-full flex-col">
        <CockpitHeader cockpit={cockpit} />

        <nav className="px-4 pt-6 sm:px-10">
          <FxSegmentedTabs
            aria-label="Bereiche der Vorlage"
            // Each of these tabs is a route: selecting as the arrows move
            // would navigate (and refetch) once per key press on the way
            // across the row.
            activation="manual"
            items={items}
            active={activeTab ?? ''}
            onSelect={(segment) =>
              navigate(segment ? `/templates/${templateId}/${segment}` : `/templates/${templateId}`)
            }
            tabWidth={200}
            className="gap-4 sm:gap-6"
          />
        </nav>

        <div className="px-4 pb-10 pt-6 sm:px-10">
          {cockpit.isLoading ? (
            <FxSpinner label="Musterformular wird geladen …" />
          ) : cockpit.error ? (
            <LoadError status={status} onRetry={() => void cockpit.refresh()} />
          ) : (
            <Outlet />
          )}
        </div>

        {/* The header's two primary actions and the shell's "+" all express
            themselves as `?dialog=…`; the dialogs are mounted here, once,
            rather than per tab — the header is the layout's and so is the
            query parameter it writes. Mounted conditionally so each opening
            starts with an empty upload queue rather than the last one's. */}
        {dialog === 'upload' && (
          <AddDocumentsDialog
            templateId={templateId}
            templateTitle={cockpit.template?.title}
            onClose={close}
          />
        )}
        <CockpitShareDialog />
      </div>
    </CockpitProvider>
  )
}

/**
 * The three ways opening a form fails, told apart, because "Fehler beim Laden"
 * sends someone to support for a link they simply may not open.
 */
function LoadError({ status, onRetry }: { status?: number; onRetry: () => void }) {
  if (status === 404) {
    return (
      <FxAlert tone="danger" title="Dieses Musterformular gibt es nicht mehr">
        Es wurde gelöscht, oder die Adresse stimmt nicht. Unter „Übersicht“ finden Sie Ihre
        Formulare.
      </FxAlert>
    )
  }
  if (status === 403) {
    return (
      <FxAlert tone="danger" title="Kein Zugriff auf dieses Musterformular">
        Bitten Sie die Person, die es angelegt hat, Sie freizugeben.
      </FxAlert>
    )
  }
  return (
    <FxAlert tone="danger" title="Das Musterformular konnte nicht geladen werden">
      <p>Möglicherweise ist die Verbindung kurz weg. Versuchen Sie es noch einmal.</p>
      <FxButton variant="outlined" className="mt-4" onClick={onRetry}>
        Erneut versuchen
      </FxButton>
    </FxAlert>
  )
}
